(() => {
  'use strict';
  if (customElements.get('gnh-video-stories')) return;
  class VideoStories extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.track = this.querySelector('[data-video-track]');
      this.cards = [...this.querySelectorAll('[data-video-card]')];
      this.prev = this.querySelector('[data-video-prev]');
      this.next = this.querySelector('[data-video-next]');
      this.nav = this.querySelector('[data-video-nav]');
      this.dots = [...this.querySelectorAll('[data-video-dot]')];
      this.cards.forEach(card => {
        const video = card.querySelector('video');
        const play = card.querySelector('[data-video-play]');
        const mute = card.querySelector('[data-video-mute]');
        const fullscreen = card.querySelector('[data-video-fullscreen]');
        const media = card.querySelector('[data-video-media]');
        video.volume = 0.5;
        play.addEventListener('click', () => this.toggle(card), { signal });
        video.addEventListener('play', () => {
          // Also cover playback started with native fullscreen controls.
          document.querySelectorAll('gnh-video-stories video').forEach(other => {
            if (other !== video) other.pause();
          });
          card.dataset.started = 'true';
          card.querySelector('[data-video-tools]').hidden = false;
          card.querySelector('[data-video-status]').textContent = '';
          this.sync(card);
        }, { signal });
        ['pause', 'ended', 'playing'].forEach(event => video.addEventListener(event, () => this.sync(card), { signal }));
        video.addEventListener('error', () => {
          card.querySelector('[data-video-status]').textContent = this.dataset.errorLabel;
          this.sync(card);
        }, { signal });
        const syncAudio = () => {
          const label = video.muted ? this.dataset.unmuteLabel : this.dataset.muteLabel;
          mute.setAttribute('aria-label', label);
          mute.title = label;
          mute.setAttribute('aria-pressed', String(video.muted));
          mute.querySelector('[data-sound-waves]').toggleAttribute('hidden', video.muted);
          mute.querySelector('[data-sound-off]').toggleAttribute('hidden', !video.muted);
        };
        mute.addEventListener('click', () => { video.muted = !video.muted; }, { signal });
        video.addEventListener('volumechange', syncAudio, { signal });
        syncAudio();
        fullscreen.hidden = !(media.requestFullscreen || video.webkitEnterFullscreen);
        fullscreen.addEventListener('click', async () => {
          try {
            if (document.fullscreenElement) await document.exitFullscreen();
            else if (media.requestFullscreen) await media.requestFullscreen();
            else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
          } catch (_) { /* Inline playback remains available when fullscreen is unavailable. */ }
        }, { signal });
        document.addEventListener('fullscreenchange', () => {
          const label = document.fullscreenElement === media ? this.dataset.exitFullscreenLabel : this.dataset.fullscreenLabel;
          fullscreen.setAttribute('aria-label', label);
          fullscreen.title = label;
        }, { signal });
      });
      this.prev.addEventListener('click', () => this.step(-1), { signal });
      this.next.addEventListener('click', () => this.step(1), { signal });
      this.track.addEventListener('scroll', () => this.updateNav(), { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.step(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.updateNav());
      this.resizeObserver.observe(this.track);
      this.visibilityObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.intersectionRatio < 0.25 && !document.fullscreenElement) entry.target.querySelector('video').pause();
      }), { threshold: 0.25 });
      this.cards.forEach(card => this.visibilityObserver.observe(card));
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pauseAll(); }, { signal });
      window.addEventListener('pagehide', () => this.pauseAll(), { signal });
      document.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('[data-video-card]');
        if (card && this.contains(card)) this.track.scrollTo({ left: card.offsetLeft - this.cards[0].offsetLeft, behavior: 'auto' });
      }, { signal });
      this.updateNav();
    }
    sync(card) {
      const video = card.querySelector('video');
      const playing = !video.paused && !video.ended;
      const label = video.error ? this.dataset.retryLabel : playing ? this.dataset.pauseLabel : video.ended ? this.dataset.replayLabel : card.dataset.started ? this.dataset.playLabel : this.dataset.initialLabel;
      card.classList.toggle('is-playing', playing);
      const button = card.querySelector('[data-video-play]');
      button.querySelector('[data-play-text]').textContent = label;
      button.querySelector('[data-play-icon]').textContent = playing ? 'Ⅱ' : '▶';
      button.setAttribute('aria-label', label + ': ' + card.getAttribute('aria-label'));
    }
    async toggle(card) {
      const video = card.querySelector('video');
      if (!video.paused) { video.pause(); return; }
      this.pauseAll();
      const button = card.querySelector('[data-video-play]');
      const status = card.querySelector('[data-video-status]');
      status.textContent = '';
      button.disabled = true;
      button.querySelector('[data-play-text]').textContent = this.dataset.loadingLabel;
      try {
        if (!video.getAttribute('src')) video.src = video.dataset.src;
        if (video.error) video.load();
        if (video.ended) video.currentTime = 0;
        await video.play();
      } catch (error) {
        if (error.name !== 'AbortError') status.textContent = this.dataset.errorLabel;
      } finally {
        button.disabled = false;
        this.sync(card);
      }
    }
    pauseAll() { this.cards.forEach(card => card.querySelector('video').pause()); }
    step(direction) {
      const width = this.cards[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap || 0);
      this.track.scrollBy({ left: direction * width, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    updateNav() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const position = this.track.scrollLeft;
      this.nav.hidden = max < 4;
      this.prev.disabled = position < 4;
      this.next.disabled = position >= max - 4;
      const width = this.cards[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap || 0);
      const active = Math.round(position / width);
      this.dots.forEach((dot, index) => dot.classList.toggle('is-active', index === active));
    }
    disconnectedCallback() {
      this.pauseAll();
      this.abort.abort();
      this.resizeObserver.disconnect();
      this.visibilityObserver.disconnect();
    }
  }
  customElements.define('gnh-video-stories', VideoStories);
})();
